import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { sliderBottom } from "d3-simple-slider";
import Loading from "../../common/components/loading/loading";
import {
  appendTimeSeriesTooltipOverlay,
  buildTooltipHtml,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipDate,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const HistoricalHeatRateLineGraph = ({
  gasData,
  poolPriceData,
  isLoading,
  initialStartDate,
  initialEndDate,
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);
  const sliderRef = useRef();

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    // Calculate dimensions based on the size of the parent container
    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20; // Further reduce slider height
    const margin = { top: 30, right: 10, bottom: 50, left: 30 };

    // Set dimensions considering margins
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    // Create SVG
    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const formatMonth = d3.timeFormat("%Y-%m");

    const calculateHeatRate = (
      data,
      dateField,
      poolPriceField,
      settlePriceField
    ) => {
      const groupedData = d3.group(data, (d) => {
        const parsedDate = new Date(d[dateField]);
        if (!parsedDate || d[dateField] === null) {
          console.error(`Failed to parse date: ${d[dateField]}`);
          return null;
        }
        return formatMonth(parsedDate);
      });

      return Array.from(groupedData, ([key, values]) => {
        if (key === null) return null;
        const avgPoolPrice = d3.mean(values, (d) => +d[poolPriceField]);
        const avgSettlePrice = d3.mean(values, (d) => +d[settlePriceField]);
        return {
          marketDate: new Date(key),
          heatRate: avgPoolPrice / avgSettlePrice,
        };
      }).filter((d) => d !== null && !isNaN(d.heatRate));
    };

    // Combine gasData and poolPriceData based on marketDate
    const combinedData = gasData
      .map((gas) => {
        const pool = poolPriceData.find(
          (pool) => pool.marketDate === gas.marketDate
        );
        return {
          marketDate: new Date(gas.marketDate), // Ensure marketDate is a Date object
          settlePrice: gas.settlePrice,
          poolPrice: pool ? pool.poolPrice : null,
        };
      })
      .filter((d) => d.poolPrice !== null);

    const heatRateData = calculateHeatRate(
      combinedData,
      "marketDate",
      "poolPrice",
      "settlePrice"
    );

    // Sort the heat rate data by marketDate
    heatRateData.sort((a, b) => a.marketDate - b.marketDate);

    // Set up scales
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(heatRateData, (d) => d.marketDate))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(heatRateData, (d) => d.heatRate)])
      .nice()
      .range([innerHeight, 0]);

    // Create line generator
    const line = d3
      .line()
      .x((d) => xScale(d.marketDate))
      .y((d) => yScale(d.heatRate))
      .curve(d3.curveMonotoneX);

    // Add X axis
    const xAxis = chartSvg
      .append("g")
      .attr("class", "x-axis d3-chart-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale));

    // Add Y axis
    const yAxis = chartSvg
      .append("g")
      .attr("class", "y-axis d3-chart-axis")
      .call(d3.axisLeft(yScale));

    // Add gridlines
    const gridlines = chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const highlightColor = dashboardSeriesColors.accentRed;
    const highlightGradientId = `heat-rate-area-${Math.random().toString(36).slice(2)}`;

    const gradient = chartSvg
      .append("defs")
      .append("linearGradient")
      .attr("id", highlightGradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", highlightColor)
      .attr("stop-opacity", 0.24);

    gradient
      .append("stop")
      .attr("offset", "38%")
      .attr("stop-color", highlightColor)
      .attr("stop-opacity", 0.09);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", highlightColor)
      .attr("stop-opacity", 0);

    const area = d3
      .area()
      .x((d) => xScale(d.marketDate))
      .y0(innerHeight)
      .y1((d) => yScale(d.heatRate))
      .curve(d3.curveMonotoneX);
    const heatRateArea = chartSvg
      .append("path")
      .datum(heatRateData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${highlightGradientId})`)
      .attr("d", area);

    // Draw heat rate line
    const heatRateLine = chartSvg
      .append("path")
      .datum(heatRateData)
      .attr("class", "line dashboard-series-primary")
      .attr("fill", "none")
      .attr("stroke", highlightColor)
      .attr("stroke-width", 2.75)
      .attr("d", line);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });
    let tooltipData = heatRateData;

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => tooltipData,
      xAccessor: (d) => d.marketDate,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.marketDate, "%Y-%m-%d"),
          rows: [
            {
              label: "Heat Rate",
              value: formatTooltipNumber(d.heatRate, { maximumFractionDigits: 2 }),
              color: dashboardSeriesColors.accentRed,
            },
          ],
        }),
    });

    // Add title
    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("Historical Heat Rate")
      .attr("class", "d3-chart-title");

    // Add slider
    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("height", 40)
      .attr("class", "no-print")
      .attr("transform", `translate(0,-20)`); // Adjust vertical position;

    sliderSvg.selectAll("*").remove();

    const sliderWidth = innerWidth; // Make slider even smaller

    const updateChart = (range) => {
      const [startDate, endDate] = range;
      const filteredData = heatRateData.filter(
        (d) => d.marketDate >= startDate && d.marketDate <= endDate,
      );
      const chartData = filteredData.length > 0 ? filteredData : heatRateData;
      tooltipData = chartData;

      xScale.domain([startDate, endDate]);
      yScale.domain([0, d3.max(chartData, (d) => d.heatRate)]).nice();

      const t = d3.transition().duration(50).ease(d3.easeLinear);

      xAxis.transition(t).call(d3.axisBottom(xScale));
      yAxis.transition(t).call(d3.axisLeft(yScale));
      gridlines
        .transition(t)
        .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

      heatRateArea.datum(chartData).transition(t).attr("d", area);
      heatRateLine.datum(chartData).transition(t).attr("d", line);
    };

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(d3.min(heatRateData, (d) => d.marketDate))
      .max(d3.max(heatRateData, (d) => d.marketDate))
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([
        initialStartDate || d3.min(heatRateData, (d) => d.marketDate),
        initialEndDate || d3.max(heatRateData, (d) => d.marketDate),
      ])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},0)`) // Adjusted vertical position to 0
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    // Call updateChart with initial values
    updateChart([
      initialStartDate || d3.min(heatRateData, (d) => d.marketDate),
      initialEndDate || d3.max(heatRateData, (d) => d.marketDate),
    ]);
  }, [gasData, poolPriceData, initialStartDate, initialEndDate]);

  // Throttle function
  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(function () {
          if (Date.now() - lastRan >= limit) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };

  useEffect(() => {
    if (!isLoading) {
      renderChart();
    }
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;

  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default HistoricalHeatRateLineGraph;


















